import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {getPublishingCtaText} from '@Components/ai-chat-window/lib/publish-copy.library';
import type {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';

/**
 * `showMessageGrowl` replaces by key, so the key decides what a growl is allowed to overwrite. One key per
 * run lets a run's own growl morph in place from "preparing" to its ready CTA, while keeping a second run
 * from overwriting a CTA the first has already delivered. A blocked pop-up is not a run — no modal ever
 * opened for it — so it keeps a fixed key and simply replaces itself.
 */
const PUBLISH_GROWL_KEY_PREFIX = 'ai-chat-publish-job';
const POPUP_BLOCKED_GROWL_KEY = `${PUBLISH_GROWL_KEY_PREFIX}-popup-blocked`;

const getRunGrowlKey = (sessionId: string): string => {
  return `${PUBLISH_GROWL_KEY_PREFIX}-${sessionId}`;
};

/**
 * Keyed per run, because a second publish can legitimately start while the first is still going — dismissing
 * the loading modal re-enables the action rows. Shared scalars let one run's completion consume or erase
 * another's handoff, silently losing a finished publish: the bug this handoff exists to prevent.
 *
 * `armedSessionId` still holds only the most recently started run, so two publishes running at once can
 * still lose the older one's handoff. Closing that needs the dismissal to be registered against the session
 * the modal is actually showing, which only `ai-action-loading-modal.library` knows — see the follow-up on a
 * shared dismiss-handoff registry.
 */
const activeSessionIds = new Set<string>();
const handedOffSessionIds = new Set<string>();
let armedSessionId: string | null = null;

/**
 * Sticky "preparing" growl shown once the user dismisses the loading modal mid-flight. Reuses the key of the
 * run's done/error growls so its pill morphs in place instead of stacking a second toast.
 */
const showPublishOngoingGrowl = (sessionId: string): void => {
  showMessageGrowl({
    key: getRunGrowlKey(sessionId),
    type: GROWL_TYPE.NEUTRAL,
    text: window.i18next.t('pmwjs_publish_preparing_design_growl'),
    isLoading: true,
    sticky: true,
    showCloseIcon: false,
  });
};

/**
 * Sticky "ready" growl carrying the per-option CTA. `sticky` and `showCloseIcon` are restated because
 * `updateMessageGrowl` rebuilds the growl from scratch on every call — an omitted field falls back to its
 * default rather than keeping the ongoing growl's value.
 */
export const showPublishDoneGrowl = (sessionId: string, option: PublishingOption, navigationUrl: string): void => {
  showMessageGrowl({
    key: getRunGrowlKey(sessionId),
    type: GROWL_TYPE.NEUTRAL,
    text: window.i18next.t('pmwjs_publish_design_ready_growl'),
    icon: 'icon-check-filled',
    iconClassName: 'content-success',
    sticky: true,
    showCloseIcon: false,
    action: {
      label: getPublishingCtaText(option),
      url: navigationUrl,
      openInNewTab: false,
    },
  });
};

/**
 * Fallback for the desktop flow when the browser refuses the new tab despite the open happening inside the
 * click. The action link is itself a user gesture, so it always succeeds where the automatic open did not.
 */
export const showPublishPopupBlockedGrowl = (option: PublishingOption, pendingPageUrl: string): void => {
  showMessageGrowl({
    key: POPUP_BLOCKED_GROWL_KEY,
    type: GROWL_TYPE.NEUTRAL,
    text: window.i18next.t('pmwjs_publish_popup_blocked'),
    showCloseIcon: true,
    action: {
      label: getPublishingCtaText(option),
      url: pendingPageUrl,
      openInNewTab: true,
    },
  });
};

export const showPublishErrorGrowl = (sessionId: string): void => {
  showMessageGrowl({
    key: getRunGrowlKey(sessionId),
    type: GROWL_TYPE.DANGER,
    text: window.i18next.t('pmwjs_error_occurred_try_again'),
    showCloseIcon: true,
  });
};

/** Arms the dismissal handoff for one publish run, identified by the loading modal's session. */
export const markPublishStarted = (sessionId: string): void => {
  activeSessionIds.add(sessionId);
  armedSessionId = sessionId;
};

/** Idempotent, so a run that settles twice cannot disarm the watcher for a sibling run still going. */
export const markPublishSettled = (sessionId: string): void => {
  activeSessionIds.delete(sessionId);

  if (armedSessionId === sessionId) {
    armedSessionId = null;
  }
};

export const isPublishInFlightForGrowlHandoff = (): boolean => {
  return activeSessionIds.size > 0;
};

export const isPublishHandedOffToGrowl = (sessionId: string): boolean => {
  return handedOffSessionIds.has(sessionId);
};

export const clearPublishHandoff = (sessionId: string): void => {
  handedOffSessionIds.delete(sessionId);
};

/**
 * Consumes the armed session so a later close — a programmatic one, or another flow's modal — cannot re-add
 * a run that has already settled and leave its entry stranded in the set.
 */
export const handlePublishModalDismissed = (): void => {
  if (armedSessionId === null) {
    return;
  }

  const dismissedSessionId = armedSessionId;
  handedOffSessionIds.add(dismissedSessionId);
  armedSessionId = null;
  showPublishOngoingGrowl(dismissedSessionId);
};
